import { RING_COLORS, RING_INKS } from "../maps/tokens";
import { dieShape, dieTilt, points } from "./shapes";

const SVG = "http://www.w3.org/2000/svg";

// "critico" lights a natural 20 on an attack, "erro" greys a natural 1.
export type DieFx = "critico" | "erro";
export type DieSvgOptions = { sides: number; value: number; color: string | null; fx?: DieFx | null };

function node(tag: string, attributes: Record<string, string | number> = {}) {
	const element = document.createElementNS(SVG, tag);
	for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, String(value));
	return element;
}

// Spelled out, since Tailwind drops a class name built by interpolation.
const SVG_CLASS: Record<DieFx | "none", string> = { none: "odo-die", critico: "odo-die odo-die--critico", erro: "odo-die odo-die--erro" };

const shade = (value: number) => (value >= 0 ? `rgba(255,255,255,${value})` : `rgba(0,0,0,${-value})`);

function label(text: string, x: number, y: number, size: number) {
	const element = node("text", { x, y, "text-anchor": "middle", "dominant-baseline": "central", "font-size": size });
	element.textContent = text;
	return element;
}

// One die, flat, lit from the top left: a body in the colour, faces lighter or darker by the
// light they take, a gloss and the number on it, the whole die slanted by its type and value.
// `value` is the numeral group, for a caller that animates the number apart from the body.
export function buildDieSvg({ sides, value, color, fx }: DieSvgOptions) {
	const shape = dieShape(sides);
	const key = RING_COLORS[color] ? color : "carmesim";
	const ink = RING_INKS[key];
	const shown = Math.abs(value);
	const svg = node("svg", { viewBox: "0 0 100 100", class: SVG_CLASS[fx ?? "none"], "aria-hidden": "true" });
	const body = node("g", { transform: `rotate(${dieTilt(sides, shown)} 50 50)` });
	body.append(node("polygon", { points: points(shape.outline), fill: RING_COLORS[key], class: "odo-die__outline" }));
	for (const [face, light] of shape.faces) body.append(node("polygon", { points: points(face), fill: shade(light), class: "odo-die__face" }));
	if (shape.gloss.length) body.append(node("polygon", { points: points(shape.gloss), class: "odo-die__gloss" }));

	const digits = String(shown).length;
	const [x, y, one, two, three] = shape.numeral;
	// Dark ink gets a light halo and the reverse, so the number reads on any face under it.
	const halo = ink === "#ffffff" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)";
	const numeral = node("g", { fill: ink, stroke: halo, "stroke-width": 1.4, class: "odo-die__numeral" });
	numeral.append(label(String(shown), x, y, digits >= 3 ? three : digits === 2 ? two : one));
	body.append(numeral);
	svg.append(body);
	return { svg: svg as SVGSVGElement, value: numeral as SVGGElement };
}
